import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import type { AdminAdvisorEditorDetail } from '../../types/admin-advisor.type';
import type { AdvisorEditorFormData } from './advisorEditor.types';
import { AdvisorInformationTab } from './AdvisorInformationTab';
import { AdvisorPreviewTab } from './AdvisorPreviewTab';

type AdvisorEditorTabsProps = {
  advisor: AdminAdvisorEditorDetail;
  formData: AdvisorEditorFormData;
  errors: Partial<Record<keyof AdvisorEditorFormData, string>>;
  isDirty: boolean;
  onChange: <K extends keyof AdvisorEditorFormData>(
    field: K,
    value: AdvisorEditorFormData[K]
  ) => void;
  onPhotoUploadingChange?: (uploading: boolean) => void;
};

export const AdvisorEditorTabs = ({
  advisor,
  formData,
  errors,
  isDirty,
  onChange,
  onPhotoUploadingChange,
}: AdvisorEditorTabsProps) => {
  const [tab, setTab] = useState<'information' | 'preview'>('information');
  const informationActive = tab === 'information';

  return (
    <Tabs
      value={tab}
      onValueChange={(value) => setTab(value as 'information' | 'preview')}
      className="w-full"
    >
      <TabsList className="w-full justify-start border-b border-gray-200 bg-transparent p-0">
        <TabsTrigger
          value="information"
          className="rounded-none border-b-2 border-transparent px-6 py-3 data-[state=active]:border-[#026857] data-[state=active]:bg-transparent data-[state=active]:text-[#026857]"
        >
          Information
        </TabsTrigger>
        <TabsTrigger
          value="preview"
          className="rounded-none border-b-2 border-transparent px-6 py-3 data-[state=active]:border-[#026857] data-[state=active]:bg-transparent data-[state=active]:text-[#026857]"
        >
          Preview
        </TabsTrigger>
      </TabsList>

      {/* forceMount: a photo upload started here must survive a switch to Preview so
          its URL still lands in form state. Radix leaves a force-mounted panel visible
          and focusable, so hide it and drop it from the a11y tree ourselves. */}
      <TabsContent
        value="information"
        className="mt-6"
        forceMount
        hidden={!informationActive}
        aria-hidden={!informationActive}
        tabIndex={informationActive ? 0 : -1}
      >
        <AdvisorInformationTab
          advisor={advisor}
          formData={formData}
          errors={errors}
          isDirty={isDirty}
          onChange={onChange}
          onPhotoUploadingChange={onPhotoUploadingChange}
        />
      </TabsContent>

      <TabsContent value="preview" className="mt-6">
        <AdvisorPreviewTab advisor={advisor} formData={formData} isDirty={isDirty} />
      </TabsContent>
    </Tabs>
  );
};
